import React, { useEffect, useMemo, useRef, useState } from "react";
import UAUCUnresolvedHeader from "./UAUCUnresolvedHeader";
import UAUCAdvancedFilters from "./UAUCAdvancedFilters";
import UAUCAssigneeSection from "./UAUCAssigneeSection";
import UAUCLoaderState from "./UAUCLoaderState";
import {
  getAssigneeIds,
  getAssigneeNames,
  getDueAt,
  getDueMeta,
  getOpenStartDate,
  isUnresolvedStatus,
  normalizeHazards,
  parseDateTimeBoundary,
  safeFetchJSON,
} from "../utils/uaucUnresolvedUtils";

const EMPTY_ADV = {
  from: "",
  to: "",
  area: "",
  minRisk: "",
  maxRisk: "",
  status: [],
  severity: [],
  overdueOnly: false,
  assignees: [],
};

const OBSERVER_BOXSHOW = ["userId", "designation", "emailId", "phone"];

export default function UAUCUnresolvedPanel({
  apiBase,
  projectId,
  onBack,
  title = "Unresolved Observations",
}) {
  const [data, setData] = useState([]);
  const [resolvedData, setResolvedData] = useState([]);
  const [ratings, setRatings] = useState({});
  const [query, setQuery] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState(EMPTY_ADV);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const abortRef = useRef(null);

  const openInNewTab = (path) => {
    const url = new URL(path, window.location.origin).toString();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openEdit = (item) => {
    const id = item?._id || item?.id;
    openInNewTab(id ? `/edit/uauc/${id}` : "/uauc");
  };

  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        const url = new URL(apiBase + "/uauc");
        url.searchParams.set("project", projectId);

        const response = await safeFetchJSON(url.toString(), {
          signal: ac.signal,
        });

        let list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        list = list.filter((item) => {
          const project = item?.project;
          const id = typeof project === "object" ? project?._id : project;
          return String(id || "") === String(projectId);
        });

        const unresolved = list.filter((item) =>
          isUnresolvedStatus(item?.status),
        );
        const resolved = list.filter(
          (item) => !isUnresolvedStatus(item?.status),
        );

        setData(unresolved);
        setResolvedData(resolved);

        const uniqueAssignees = [
          ...new Set(
            list.flatMap((item) => getAssigneeIds(item)).filter(Boolean),
          ),
        ];

        const ratingResults = await Promise.allSettled(
          uniqueAssignees.map(async (assigneeId) => {
            const rating = await safeFetchJSON(
              `${apiBase}/uauc/rating/${assigneeId}`,
              { signal: ac.signal },
            );

            return [assigneeId, rating];
          }),
        );

        const nextRatings = {};
        ratingResults.forEach((result) => {
          if (result.status === "fulfilled") {
            const [assigneeId, rating] = result.value;
            nextRatings[assigneeId] = rating;
          }
        });

        setRatings(nextRatings);
      } catch (error) {
        if (error.name !== "AbortError") {
          setErr(error.message || "Failed to load unresolved observations.");
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId]);

  const assignees = useMemo(() => {
    const map = new Map();

    for (const item of data) {
      const ids = getAssigneeIds(item);
      const names = getAssigneeNames(item);

      ids.forEach((id, index) => {
        if (!id) return;
        if (!map.has(id)) map.set(id, names[index] || "Unnamed");
      });
    }

    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return (data || [])
      .filter((item) => {
        if (!q) return true;

        const searchText = [
          item?.observer?.name,
          item?.area,
          item?.comment,
          item?.observation,
          normalizeHazards(item?.hazards),
          item?.status,
          item?.riskValue?.severity,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchText.includes(q);
      })
      .filter((item) => {
        if (!assignedToId) return true;
        return getAssigneeIds(item).map(String).includes(String(assignedToId));
      })
      .filter((item) => {
        if (!adv.assignees?.length) return true;

        const ids = getAssigneeIds(item).map(String);
        return adv.assignees.some((id) => ids.includes(String(id)));
      })
      .filter((item) => {
        const opened = getOpenStartDate(item);
        const openedTime = opened ? new Date(opened).getTime() : 0;

        if (adv.from) {
          const fromDate = parseDateTimeBoundary(adv.from, false);
          const fromTime = fromDate ? fromDate.getTime() : 0;
          if (openedTime && openedTime < fromTime) return false;
        }

        if (adv.to) {
          const toDate = parseDateTimeBoundary(adv.to, true);
          const toTime = toDate ? toDate.getTime() : Number.POSITIVE_INFINITY;
          if (openedTime && openedTime > toTime) return false;
        }

        return true;
      })
      .filter((item) => {
        if (!adv.area) return true;
        return String(item?.area || "")
          .toLowerCase()
          .includes(adv.area.toLowerCase());
      })
      .filter((item) => {
        const riskValue = Number(item?.riskValue?.value ?? NaN);

        if (
          adv.minRisk &&
          !Number.isNaN(riskValue) &&
          riskValue < Number(adv.minRisk)
        ) {
          return false;
        }

        if (
          adv.maxRisk &&
          !Number.isNaN(riskValue) &&
          riskValue > Number(adv.maxRisk)
        ) {
          return false;
        }

        return true;
      })
      .filter((item) => {
        if (!adv.severity?.length) return true;

        const severity = String(item?.riskValue?.severity || "").toLowerCase();
        return adv.severity.some(
          (itemSeverity) => severity === String(itemSeverity).toLowerCase(),
        );
      })
      .filter((item) => {
        if (!adv.status?.length) return true;

        const status = String(item?.status || "").toLowerCase();
        return adv.status.some((itemStatus) =>
          status.includes(String(itemStatus).toLowerCase()),
        );
      })
      .filter((item) => {
        if (!adv.overdueOnly) return true;

        const dueAt = getDueAt(item);
        return dueAt ? Date.now() > dueAt.getTime() : false;
      })
      .sort((a, b) => {
        const metaA = getDueMeta(a);
        const metaB = getDueMeta(b);

        if (metaA.overdue !== metaB.overdue) {
          return metaA.overdue ? -1 : 1;
        }

        if (metaA.dueAt && metaB.dueAt) {
          return metaA.dueAt - metaB.dueAt;
        }

        const openedA = new Date(getOpenStartDate(a) || 0).getTime();
        const openedB = new Date(getOpenStartDate(b) || 0).getTime();

        return openedA - openedB;
      });
  }, [data, query, assignedToId, adv]);

  const groupedByAssignee = useMemo(() => {
    const groups = new Map();

    const addItem = (id, name, item) => {
      if (!groups.has(id)) {
        groups.set(id, {
          id,
          name: name || "Unassigned",
          items: [],
        });
      }

      groups.get(id).items.push(item);
    };

    for (const item of filtered) {
      const ids = getAssigneeIds(item);
      const names = getAssigneeNames(item);

      if (!ids.length) {
        addItem("__unassigned__", "Unassigned", item);
      } else {
        ids.forEach((id, index) => {
          addItem(id, names[index] || "Unnamed", item);
        });
      }
    }

    for (const group of groups.values()) {
      group.items.sort((a, b) => {
        const openedA = new Date(getOpenStartDate(a) || 0).getTime();
        const openedB = new Date(getOpenStartDate(b) || 0).getTime();

        return openedA - openedB;
      });
    }

    return Array.from(groups.values()).sort((a, b) => {
      if (a.id === "__unassigned__") return -1;
      if (b.id === "__unassigned__") return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered]);

  const dashboardStats = useMemo(() => {
    const overdue = filtered.filter((item) => {
      const dueAt = getDueAt(item);
      return dueAt && Date.now() > dueAt.getTime();
    }).length;

    const critical = filtered.filter((item) => {
      const value = Number(item?.riskValue?.value);
      const severity = String(item?.riskValue?.severity || "").toLowerCase();

      return (
        value >= 4 || severity.includes("critical") || severity.includes("high")
      );
    }).length;

    return {
      total: filtered.length,
      assignees: groupedByAssignee.length,
      overdue,
      critical,
    };
  }, [filtered, groupedByAssignee]);

  const bodyState = (
    <UAUCLoaderState
      loading={loading}
      error={err}
      empty={!loading && !err && groupedByAssignee.length === 0}
    />
  );

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <UAUCUnresolvedHeader
        title={title}
        query={query}
        setQuery={setQuery}
        assignees={assignees}
        assignedToId={assignedToId}
        setAssignedToId={setAssignedToId}
        advOpen={advOpen}
        setAdvOpen={setAdvOpen}
        onBack={onBack}
        stats={dashboardStats}
      />

      {advOpen && (
        <UAUCAdvancedFilters adv={adv} setAdv={setAdv} assignees={assignees} />
      )}

      <div className="p-4">
        {loading || err || groupedByAssignee.length === 0 ? (
          bodyState
        ) : (
          <div className="space-y-5">
            {groupedByAssignee.map((section) => (
              <UAUCAssigneeSection
                key={section.id}
                section={section}
                ratings={ratings}
                observerBoxShow={OBSERVER_BOXSHOW}
                onOpenEdit={openEdit}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
